import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import RequestFundingSummary, { fundingGate } from './RequestFundingSummary';
import './pendingClientRequests.css';

const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Field metadata for both edit types. `pricing: true` fields get an
// "affects pricing" tag in the diff (PLAN D10 — flag prominently).
const yesNo = (v) => (v === true || v === 'true' ? 'Yes' : 'No');

const FIELD_META = {
    booking_start: { label: 'Start time', format: (v) => (v ? String(v).slice(0, 16) : '—') },
    duration_hours: { label: 'Duration', format: (v) => (v == null ? '—' : `${v} hrs`), pricing: true },
    required_cleaners: { label: 'Cleaners', format: (v) => (v == null ? '—' : String(v)), pricing: true },
    type_id: { label: 'Service', valueKey: 'type_label', format: (v) => (v ? String(v) : '—'), pricing: true },
    property_id: { label: 'Property', valueKey: 'property_label', format: (v) => (v ? String(v) : '—'), pricing: true },
    notes: { label: 'Notes', format: (v) => (v ? String(v) : '—') },
    address_line_1: { label: 'Building', format: (v) => (v ? String(v) : '—') },
    unit_number: { label: 'Unit', format: (v) => (v ? String(v) : '—') },
    area: { label: 'Area', format: (v) => (v ? String(v) : '—') },
    bedrooms: { label: 'Bedrooms', format: (v) => (v === 0 ? 'Studio' : String(v ?? '—')), pricing: true },
    bathrooms: { label: 'Bathrooms', format: (v) => String(v ?? '—') },
    single_beds: { label: 'Single beds', format: (v) => String(v ?? '—'), pricing: true },
    double_beds: { label: 'Double beds', format: (v) => String(v ?? '—'), pricing: true },
    super_king_beds: { label: 'Super King beds', format: (v) => String(v ?? '—'), pricing: true },
    is_beach_towel: { label: 'Beach towels', format: yesNo, pricing: true },
    is_dolce_gusto: { label: 'Dolce Gusto', format: yesNo, pricing: true },
    is_nespresso: { label: 'Nespresso', format: yesNo, pricing: true },
    property_access: { label: 'Access notes', format: (v) => (v ? String(v) : '—') },
    wifi_name: { label: 'WiFi name', format: (v) => (v ? String(v) : '—') },
    wifi_password: { label: 'WiFi password', format: (v) => (v ? String(v) : '—') },
    location: { label: 'Map location', valueKey: 'location_label', format: (v) => (v ? String(v) : '—') },
    setup_photos: { label: 'Setup photos', format: (v) => (Array.isArray(v) && v.length ? `${v.length} added` : '—') },
    // Client profile (edit_client_profile) — all sync to Zoho on approval.
    client_type: { label: 'Account type', format: (v) => (v === 'business' ? 'Business' : 'Individual') },
    company_name: { label: 'Company name', format: (v) => (v ? String(v) : '—') },
    contact_number: { label: 'Contact number', format: (v) => (v ? String(v) : '—') },
    trn_number: { label: 'TRN number', format: (v) => (v ? String(v) : '—') },
    is_vat_registered: { label: 'VAT registered', format: yesNo },
    city: { label: 'City', format: (v) => (v ? String(v) : '—') },
    state: { label: 'State / Emirate', format: (v) => (v ? String(v) : '—') },
    country: { label: 'Country', format: (v) => (v ? String(v) : '—') },
    documents: { label: 'Documents', format: (v) => (Array.isArray(v) && v.length ? `${v.length} uploaded` : '—') },
};

const norm = (v) => (v == null ? '' : String(v).trim());
const differs = (a, b) => {
    const na = Number(a); const nb = Number(b);
    if (norm(a) !== '' && norm(b) !== '' && Number.isFinite(na) && Number.isFinite(nb)) return na !== nb;
    return norm(a) !== norm(b);
};

// Review modal for edit_booking / edit_property requests: side-by-side
// Original | Requested diff of the changed fields, with drift warnings when
// the live target changed since the client submitted. Approve-as-is or reject.
export default function EditRequestReview({ request, onClose, onDone, readOnlyOutcome = null }) {
    const isBooking = request.request_type === 'edit_booking';
    const isProfile = request.request_type === 'edit_client_profile';
    const isTasks = request.request_type === 'edit_client_tasks';
    const [detail, setDetail] = useState(null); // request row + .live from GET /:id
    const [loading, setLoading] = useState(true);
    const [rejectMode, setRejectMode] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const rejectReasonRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`/api/client-change-requests/${request.id}`);
                setDetail(res.data?.request || null);
            } catch (e) {
                console.error('Error loading request detail:', e);
                setError('Failed to load the request details.');
            } finally {
                setLoading(false);
            }
        })();
    }, [request.id]);

    useEffect(() => {
        if (rejectMode && rejectReasonRef.current) {
            rejectReasonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            rejectReasonRef.current.focus();
        }
    }, [rejectMode]);

    const approve = async () => {
        setSubmitting(true);
        setError('');
        try {
            const res = await axios.post(`/api/client-change-requests/${request.id}/approve`, {});
            onDone('approved', res.data?.message, res.data?.result_id);
        } catch (err) {
            console.error('Error approving edit request:', err);
            setError(err.response?.data?.error || 'Failed to approve request.');
        } finally {
            setSubmitting(false);
        }
    };

    const reject = async () => {
        if (!rejectReason.trim()) {
            setError('A rejection reason is required.');
            rejectReasonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            rejectReasonRef.current?.focus();
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            await axios.post(`/api/client-change-requests/${request.id}/reject`, { reason: rejectReason.trim() });
            onDone('rejected');
        } catch (err) {
            console.error('Error rejecting edit request:', err);
            setError(err.response?.data?.error || 'Failed to reject request.');
        } finally {
            setSubmitting(false);
        }
    };

    const p = (detail || request).payload || {};
    const snapshot = (detail || request).original_snapshot || {};
    const live = detail?.live || null;
    const livePhotos = detail?.live_photos || null; // { [id]: { description } } for touched existing photos
    const changed = Array.isArray(p.changed_fields) ? p.changed_fields : [];

    // Is the request itself still actionable, and is the live target still in an
    // approvable state? (Approve would 409 — tell the admin up front so they
    // reject / close instead of hitting the wall.)
    let blockedNote = null;
    const requestStatus = detail?.status;
    if (requestStatus && requestStatus !== 'pending') {
        blockedNote = requestStatus === 'cancelled'
            ? 'The client has cancelled this request.'
            : `This request has already been ${requestStatus}.`;
    } else if (isBooking && live) {
        const status = String(live.status_name || '').trim().toLowerCase();
        const nowDubai = DateTime.now().setZone('Asia/Dubai').toFormat('yyyy-MM-dd HH:mm:ss');
        if (live.is_deleted || status === 'deleted') blockedNote = 'This booking has been deleted since the request was made.';
        else if (status !== 'confirmed') blockedNote = `This booking is now ${live.status_name} and can no longer be changed.`;
        else if (live.invoice_id != null) blockedNote = 'This booking has been invoiced and can no longer be changed.';
        else if (!live.future_ok) blockedNote = 'This booking has already started or passed.';
        else if (changed.includes('booking_start') && String(p.booking_start || '') <= nowDubai) {
            blockedNote = 'The requested new time has already passed.';
        }
    } else if (!isBooking && live && live.is_active === false) {
        blockedNote = 'This property is no longer active.';
    }

    // Stale-estimate warning: pricing-relevant fields the client did NOT change
    // (so they never appear in the diff) that have drifted since the request make
    // the price panel's request-time numbers unreliable.
    const pricingDrift = isBooking && live && !blockedNote
        && ['duration_hours', 'required_cleaners', 'type_label'].some(
            (f) => !changed.includes(f === 'type_label' ? 'type_id' : f)
                && f in live && differs(live[f], snapshot[f])
        );

    // Last-minute fork: original kept as Last Minute Rescheduled + NEW booking.
    // Judged by the REQUEST-TIME flag (an on-time request is never penalised by
    // a slow review), stored on the payload at submission.
    const lmReschedule = isBooking && !blockedNote && p.is_last_minute_at_request === true
        && changed.includes('booking_start');

    const priceDelta = isBooking && p.price_delta != null ? Number(p.price_delta) : null;

    // A non-pending request can't be approved OR rejected any more.
    const requestGone = !!(requestStatus && requestStatus !== 'pending');

    // F5. `funding_error` always blocks — the server could not prove where the
    // money would come from. Blockers block only when the delta is POSITIVE,
    // which is exactly the condition under which the edit approve branch runs
    // its funding check (`editPriceDelta > 0.009`). A price-reducing edit
    // refunds money and is never gated on funding, so mirroring that here keeps
    // the button honest instead of blocking a refund the server would accept.
    const gate = fundingGate(request, detail, {
        enforceBlockers: priceDelta != null && priceDelta > 0.01,
    });
    // Never dismiss mid-flight — a failure after unmount would be silent.
    const safeClose = () => { if (!submitting) onClose(); };

    return (
        <div className="pcr-modal-overlay" onClick={safeClose}>
            <div className="pcr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="pcr-modal-header">
                    <div>
                        <h3>{isBooking ? 'Review Booking Change' : isProfile ? 'Review Account Change' : isTasks ? 'Review Task Change' : 'Review Property Change'}</h3>
                        <div className="pcr-modal-sub">
                            {request.client_display_name || request.client_company_name} · {p.property_label || `#${request.target_id}`}
                            {isBooking ? ` · booking #${request.target_id}` : ''} · requested by {request.requested_by_first_name} {request.requested_by_last_name}
                        </div>
                    </div>
                    <button className="pcr-modal-close" onClick={safeClose} title="Close">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="pcr-modal-body">
                    {loading ? (
                        <div className="pcr-empty">Loading request…</div>
                    ) : (
                        <>
                            {blockedNote && (
                                <div className="pcr-error" style={{ marginBottom: 12 }}>
                                    <FontAwesomeIcon icon={faTriangleExclamation} /> {blockedNote}
                                    {!requestGone && ' Approving will fail — reject the request instead.'}
                                </div>
                            )}

                            <div className="pcr-section-title">Requested changes</div>
                            <div className="pcr-diff">
                                <div className="pcr-diff-row pcr-diff-head">
                                    <span>Field</span>
                                    <span>Original</span>
                                    <span>Requested</span>
                                </div>
                                {changed.map((field) => {
                                    // Setup photos: one summary row covering additions,
                                    // removals and note changes (details shown below).
                                    if (field === 'setup_photos') {
                                        const added = Array.isArray(p.setup_photos) ? p.setup_photos.length : 0;
                                        const removed = Array.isArray(p.setup_photo_removals) ? p.setup_photo_removals.length : 0;
                                        const noted = Array.isArray(p.setup_photo_updates) ? p.setup_photo_updates.length : 0;
                                        const toggles = Array.isArray(p.setup_photo_task_toggles) ? p.setup_photo_task_toggles : [];
                                        const taskOn = toggles.filter((t) => t.make_task).length;
                                        const taskOff = toggles.filter((t) => !t.make_task).length;
                                        const parts = [];
                                        if (added) parts.push(`${added} added`);
                                        if (removed) parts.push(`${removed} removed`);
                                        if (noted) parts.push(`${noted} note${noted > 1 ? 's' : ''} updated`);
                                        if (taskOn) parts.push(`${taskOn} → task`);
                                        if (taskOff) parts.push(`${taskOff} task removed`);
                                        return (
                                            <div className="pcr-diff-row" key={field}>
                                                <span className="pcr-diff-field">Setup photos</span>
                                                <span className="pcr-diff-old">—</span>
                                                <span className="pcr-diff-new">{parts.join(' · ') || '—'}</span>
                                            </div>
                                        );
                                    }
                                    // Task list changes get their own before/after
                                    // section below — show a one-line summary row here.
                                    if (field === 'client_tasks' || field === 'property_tasks' || field === 'tasks') {
                                        const desired = Array.isArray(p[field]) ? p[field] : [];
                                        return (
                                            <div className="pcr-diff-row" key={field}>
                                                <span className="pcr-diff-field">Tasks</span>
                                                <span className="pcr-diff-old">—</span>
                                                <span className="pcr-diff-new">{desired.length} task{desired.length === 1 ? '' : 's'} after change (see below)</span>
                                            </div>
                                        );
                                    }
                                    let meta = FIELD_META[field] || { label: field, format: (v) => String(v ?? '—') };
                                    // address_line_1 is "Building" for properties but the
                                    // billing "Address" for a client-profile edit.
                                    if (isProfile && field === 'address_line_1') meta = { ...meta, label: 'Address' };
                                    // Some fields (service/property) display via a label
                                    // field rather than the raw id.
                                    const vKey = meta.valueKey || field;
                                    const hasDrift = live && vKey in live && differs(live[vKey], snapshot[vKey]);
                                    return (
                                        <React.Fragment key={field}>
                                            <div className="pcr-diff-row">
                                                <span className="pcr-diff-field">
                                                    {meta.label}
                                                    {meta.pricing && <span className="pcr-diff-pricing-tag">affects pricing</span>}
                                                </span>
                                                <span className="pcr-diff-old">{meta.format(snapshot[vKey])}</span>
                                                <span className="pcr-diff-new">{meta.format(p[vKey])}</span>
                                            </div>
                                            {hasDrift && (
                                                <div className="pcr-diff-drift">
                                                    <FontAwesomeIcon icon={faTriangleExclamation} />{' '}
                                                    Changed since the request: it is now <strong>{meta.format(live[vKey])}</strong>.
                                                    Approving applies the client&apos;s requested value above.
                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>

                            {(() => {
                                // Resolve the before/after task lists for whichever
                                // task-bearing edit this is.
                                let present = false; let oldList = []; let newList = []; let heading = 'Tasks';
                                if (isTasks && Array.isArray(p.tasks)) {
                                    present = true; oldList = snapshot.tasks || []; newList = p.tasks; heading = 'All-units tasks';
                                } else if (Array.isArray(p.property_tasks)) {
                                    present = true; oldList = p.property_tasks_original || []; newList = p.property_tasks; heading = 'Property tasks';
                                } else if (Array.isArray(p.booking_tasks)) {
                                    present = true; oldList = p.booking_tasks_original || []; newList = p.booking_tasks; heading = 'Booking tasks';
                                }
                                if (!present) return null;
                                const renderList = (list) => (list.length ? (
                                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                                        {list.map((t, i) => (
                                            <li key={i} style={{ fontSize: 13, color: '#374151', marginBottom: 4 }}>
                                                {t.instruction}{t.is_report_required ? ' 📷' : ''}
                                            </li>
                                        ))}
                                    </ul>
                                ) : <div className="pcr-empty" style={{ fontSize: 13 }}>None</div>);
                                return (
                                    <div className="pcr-section" style={{ marginTop: 14 }}>
                                        <div className="pcr-section-title">{heading} — before → after</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                            <div>
                                                <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Before ({oldList.length})</div>
                                                {renderList(oldList)}
                                            </div>
                                            <div>
                                                <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', marginBottom: 6 }}>After ({newList.length})</div>
                                                {renderList(newList)}
                                            </div>
                                        </div>
                                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>📷 = cleaner must upload a photo</div>
                                    </div>
                                );
                            })()}

                            {Array.isArray(p.setup_photos) && p.setup_photos.length > 0 && (
                                <div className="pcr-section" style={{ marginTop: 14 }}>
                                    <div className="pcr-section-title">New setup photos ({p.setup_photos.length})</div>
                                    <div className="pcr-photos">
                                        {p.setup_photos.map((photo, i) => (
                                            <a className="pcr-photo" key={photo.file_key || i} href={photo.file_url} target="_blank" rel="noopener noreferrer">
                                                <img src={photo.file_url} alt={`Setup ${i + 1}`} />
                                                {photo.description && <div className="pcr-photo-desc">{photo.description}</div>}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {Array.isArray(p.setup_photo_removals) && p.setup_photo_removals.length > 0 && (
                                <div className="pcr-section" style={{ marginTop: 14 }}>
                                    <div className="pcr-section-title">Photos to remove ({p.setup_photo_removals.length})</div>
                                    <div className="pcr-photos">
                                        {p.setup_photo_removals.map((photo, i) => {
                                            const gone = livePhotos && !livePhotos[photo.id];
                                            return (
                                                <a className="pcr-photo pcr-photo--removed" key={photo.id || i} href={photo.file_url} target="_blank" rel="noopener noreferrer">
                                                    <img src={photo.file_url} alt={`To remove ${i + 1}`} />
                                                    <div className="pcr-photo-desc pcr-photo-desc--removed">
                                                        {gone ? 'Already removed since the request' : 'Will be removed'}
                                                    </div>
                                                </a>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {Array.isArray(p.setup_photo_updates) && p.setup_photo_updates.length > 0 && (
                                <div className="pcr-section" style={{ marginTop: 14 }}>
                                    <div className="pcr-section-title">Photo note changes ({p.setup_photo_updates.length})</div>
                                    <div className="pcr-photonotes">
                                        {p.setup_photo_updates.map((upd, i) => {
                                            const lp = livePhotos ? livePhotos[upd.id] : null;
                                            const gone = livePhotos && !lp;
                                            const drifted = lp && (lp.description || '') !== (upd.old_description || '');
                                            return (
                                                <div className="pcr-photonote" key={upd.id || i}>
                                                    <a href={upd.file_url} target="_blank" rel="noopener noreferrer">
                                                        <img src={upd.file_url} alt={`Note change ${i + 1}`} />
                                                    </a>
                                                    <div className="pcr-photonote-text">
                                                        <div className="pcr-photonote-old">{upd.old_description || '(no note)'}</div>
                                                        <div className="pcr-photonote-new">{upd.description || '(note removed)'}</div>
                                                        {gone && (
                                                            <div className="pcr-photonote-drift">
                                                                This photo was removed since the request — nothing will change.
                                                            </div>
                                                        )}
                                                        {drifted && (
                                                            <div className="pcr-photonote-drift">
                                                                Note changed since the request: it is now &ldquo;{lp.description || '(no note)'}&rdquo;.
                                                                Approving applies the client&apos;s version above.
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {Array.isArray(p.setup_photo_task_toggles) && p.setup_photo_task_toggles.length > 0 && (
                                <div className="pcr-section" style={{ marginTop: 14 }}>
                                    <div className="pcr-section-title">Permanent task changes ({p.setup_photo_task_toggles.length})</div>
                                    <div className="pcr-photos">
                                        {p.setup_photo_task_toggles.map((tog, i) => (
                                            <a className="pcr-photo" key={tog.id || i} href={tog.file_url} target="_blank" rel="noopener noreferrer">
                                                <img src={tog.file_url} alt={`Task toggle ${i + 1}`} />
                                                <div className="pcr-photo-desc" style={{ fontWeight: 700, color: tog.make_task ? '#1d4ed8' : '#b91c1c' }}>
                                                    {tog.make_task ? 'Make a permanent task' : 'Remove permanent task'}
                                                </div>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {Array.isArray(p.documents) && p.documents.length > 0 && (
                                <div className="pcr-section" style={{ marginTop: 14 }}>
                                    <div className="pcr-section-title">Uploaded documents ({p.documents.length})</div>
                                    <div className="pcr-photos">
                                        {p.documents.map((doc, i) => (
                                            <a className="pcr-photo" key={doc.file_key || i} href={doc.file_url} target="_blank" rel="noopener noreferrer">
                                                {/\.pdf($|\?)/i.test(doc.file_url)
                                                    ? <div className="pcr-photo-pdf">PDF</div>
                                                    : <img src={doc.file_url} alt={`Document ${i + 1}`} />}
                                                <div className="pcr-photo-desc">
                                                    {doc.type === 'trade_license' ? 'Trade License'
                                                        : doc.type === 'vat_certificate' ? 'VAT Certificate' : 'Document'}
                                                </div>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {isProfile && (
                                <div className="pcr-hint" style={{ marginTop: 12 }}>
                                    Approving updates the client&apos;s billing/tax details and syncs them to Zoho Books.
                                </div>
                            )}

                            {pricingDrift && (
                                <div className="pcr-hint" style={{ marginTop: 12 }}>
                                    <FontAwesomeIcon icon={faTriangleExclamation} />{' '}
                                    This booking was repriced or edited since the request was made — the
                                    price figures below are from request time and may be stale. The real
                                    difference is charged/refunded from the live booking on approval.
                                </div>
                            )}

                            {lmReschedule && (
                                <div className="pcr-hint" style={{ marginTop: 12 }}>
                                    <FontAwesomeIcon icon={faTriangleExclamation} />{' '}
                                    <strong>Last-minute reschedule:</strong> the visit starts within 24 hours,
                                    so approving keeps the original booking as <strong>Last Minute
                                    Rescheduled</strong>{p.penalty_fee_incl_vat ? <> (fee {money(p.penalty_fee_incl_vat)} kept, difference refunded)</> : ' (the LM fee is kept, difference refunded)'} and
                                    creates a <strong>new booking</strong> at the requested time, charged fresh.
                                </div>
                            )}

                            {!isBooking && detail?.price_preview && (
                                <div className="pcr-price-summary" style={{ marginTop: 14 }}>
                                    <div className="pcr-price-main">
                                        <strong>
                                            {detail.price_preview.must_use_custom_price && detail.price_preview.total != null
                                                ? money(detail.price_preview.total)
                                                : 'Standard rate'}
                                        </strong>
                                        <span>all-inclusive price if approved (excl. VAT)</span>
                                    </div>
                                    <div className="pcr-price-breakdown">
                                        {Array.isArray(detail.price_preview.breakdown) && detail.price_preview.breakdown.map((row) => (
                                            <div className="pcr-price-row" key={row.label}>
                                                <span>{row.label}</span>
                                                <span>{money(row.amount)}</span>
                                            </div>
                                        ))}
                                        {detail.price_preview.total_incl_vat != null && (
                                            <div className="pcr-price-row" style={{ fontWeight: 700 }}>
                                                <span>Total incl. VAT</span>
                                                <span>{money(detail.price_preview.total_incl_vat)}</span>
                                            </div>
                                        )}
                                        <div className="pcr-price-row" style={{ marginTop: 6, paddingTop: 6, borderTop: '1px dashed #e5e7eb' }}>
                                            <span>Current price</span>
                                            <span>
                                                {detail.price_preview.current_is_custom && detail.price_preview.current_custom_price != null
                                                    ? money(detail.price_preview.current_custom_price)
                                                    : 'Standard rate'}
                                            </span>
                                        </div>
                                        <div className="pcr-price-row">
                                            <span>Approving updates the property&apos;s pricing to the value above</span>
                                            <span />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {isBooking && (
                                <div className="pcr-price-summary" style={{ marginTop: 14 }}>
                                    <div className="pcr-price-main">
                                        <strong>{money(request.estimated_total)}</strong>
                                        <span>estimated new price (incl. VAT)</span>
                                    </div>
                                    <div className="pcr-price-breakdown">
                                        <div className="pcr-price-row">
                                            <span>Current booking total</span>
                                            <span>{money(snapshot.current_total)}</span>
                                        </div>
                                        {/* PRICE language only (A5): "Price increase" is the
                                            arithmetic fact; HOW it is paid — wallet, card,
                                            charged on approval — is stated once, by the
                                            payment list below. "Refund to wallet on approval"
                                            keeps its wording because a price-reducing edit
                                            renders no payment list, so this row is the only
                                            statement of the refund. */}
                                        {priceDelta != null && Math.abs(priceDelta) > 0.01 && (
                                            <div className="pcr-price-row" style={{ fontWeight: 700, color: priceDelta > 0 ? '#b45309' : '#047857' }}>
                                                <span>{priceDelta > 0 ? 'Price increase' : 'Refund to wallet on approval'}</span>
                                                <span>{priceDelta > 0 ? '+' : '−'}{money(Math.abs(priceDelta))}</span>
                                            </div>
                                        )}
                                        {priceDelta != null && Math.abs(priceDelta) <= 0.01 && (
                                            <div className="pcr-price-row">
                                                <span>Price change</span>
                                                <span>None</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* A5 (2026-07-19): ONE clear payment list,
                                AFTER the price summary — one row per funding leg,
                                balance-after when the wallet is debited on approval,
                                one explanatory line. The rail banner, the inline
                                wallet figure and the pending-deposit paragraph are
                                gone: their facts live in this list and its blockers. */}
                            {isBooking && priceDelta != null && priceDelta > 0.01 && (
                                <RequestFundingSummary readOnly={!!readOnlyOutcome}
                                    request={request}
                                    detail={detail}
                                    cost={priceDelta}
                                    deltaLabel="Extra to pay for this change"
                                />
                            )}

                            {rejectMode && (
                                <div className="pcr-section pcr-reason-block pcr-reason-block--reject">
                                    <div className="pcr-section-title">Rejection reason (visible to client)</div>
                                    <div className="pcr-reason-presets">
                                        <button
                                            type="button"
                                            className="pcr-reason-preset"
                                            onClick={() => setRejectReason('No availability at the requested time.')}
                                        >
                                            No availability
                                        </button>
                                    </div>
                                    <textarea ref={rejectReasonRef} className="pcr-reject-reason" value={rejectReason} maxLength={500} placeholder="Explain why this change is being rejected…" onChange={(e) => setRejectReason(e.target.value)} />
                                </div>
                            )}

                            {gate.blocked && (
                                <div className="pcr-error">
                                    <FontAwesomeIcon icon={faTriangleExclamation} /> {gate.reason}
                                </div>
                            )}

                            {!!error && <div className="pcr-error">{error}</div>}
                        </>
                    )}
                </div>

                {/* Read-only history view (2026-08-06): rejected/
                    cancelled/approved rows open for inspection — outcome
                    instead of action buttons. */}
                {readOnlyOutcome ? (
                    <div className="pcr-modal-actions" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <div style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>
                            <strong style={{ textTransform: 'capitalize' }}>{readOnlyOutcome.statusLabel}</strong>
                            {readOnlyOutcome.reviewerName ? <> by <strong>{readOnlyOutcome.reviewerName}</strong></> : null}
                            {readOnlyOutcome.reviewedAt ? <> · {readOnlyOutcome.reviewedAt}</> : null}
                            {readOnlyOutcome.reason ? <div><strong>Reason:</strong> {readOnlyOutcome.reason}</div> : null}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {readOnlyOutcome.onOpenEntity ? (
                                <button className="pcr-btn pcr-btn--primary" onClick={readOnlyOutcome.onOpenEntity}>
                                    {readOnlyOutcome.entityLabel}
                                </button>
                            ) : null}
                            <button className="pcr-btn pcr-btn--ghost" onClick={onClose}>Close</button>
                        </div>
                    </div>
                ) : (
                <div className="pcr-modal-actions">
                    {rejectMode ? (
                        <>
                            <button className="pcr-btn pcr-btn--ghost" onClick={() => { setRejectMode(false); setError(''); }} disabled={submitting}>Back</button>
                            <button className="pcr-btn pcr-btn--danger" onClick={reject} disabled={submitting}>{submitting ? 'Rejecting…' : 'Confirm Reject'}</button>
                        </>
                    ) : (
                        <>
                            <button className="pcr-btn pcr-btn--ghost pcr-btn--danger-text" onClick={() => { setRejectMode(true); setError(''); }} disabled={submitting || loading || requestGone}>Reject</button>
                            <button
                                className="pcr-btn pcr-btn--primary"
                                onClick={approve}
                                disabled={submitting || loading || !!blockedNote || gate.blocked}
                                title={gate.blocked ? gate.reason : undefined}
                            >
                                {submitting ? 'Applying…' : 'Approve Change'}
                            </button>
                        </>
                    )}
                </div>
                )}
            </div>
        </div>
    );
}
